import { create } from "zustand"
import { DISCORD_INVITE_URL } from "@/lib/discord-invite"
import { persist } from "zustand/middleware"
import { isAdTestModeOn, isAdCooldownExpired } from "./ad-cooldown"

export interface AdSlot {
  id: string
  name: string
  slug: string
  description?: string
  slot_type: "banner" | "video_preroll" | "video_midroll" | "interstitial" | "native" | "grid_inline"
  position?: string
  dimensions?: string
  is_active: boolean
  priority: number
  settings: Record<string, unknown>
}

export interface AdNetwork {
  id: string
  name: string
  slug: string
  network_type: "exoclick" | "propeller" | "trafficjunky" | "custom" | "affiliate"
  publisher_id?: string
  is_active: boolean
  default_cpm: number
  payout_percentage: number
  settings: Record<string, unknown>
}

export interface AdCampaign {
  id: string
  name: string
  network_id?: string
  campaign_type: "network" | "internal" | "affiliate"
  status: "draft" | "active" | "paused" | "completed" | "archived"
  embed_code?: string
  image_url?: string
  video_url?: string
  click_url?: string
  alt_text?: string
  target_slots: string[]
  target_user_types: string[]
  target_devices: string[]
  start_date?: string
  end_date?: string
  daily_impression_cap?: number
  total_impression_cap?: number
  cpm_rate: number
  cpc_rate: number
  ab_test_id?: string
  ab_variant?: string
  traffic_weight: number
}

export interface InlineAdCampaign extends AdCampaign {
  title?: string
  subtitle?: string
  cta_text?: string
  badge_text?: string
  badge_color?: string
  background_gradient?: string
  promo_code?: string
  discount_text?: string
}

export interface AdImpression {
  campaign_id: string
  /** House ads have text ids, not uuids, so they attribute via this instead
   * of campaign_id — see migration 20261114000000. */
  house_ad_id?: string
  slot_id: string
  user_id?: string
  session_id: string
  page_url: string
  device_type: string
  viewable: boolean
  view_duration_ms: number
}

export interface AdStats {
  impressions: number
  clicks: number
  ctr: number
  estimated_revenue: number
  viewability_rate: number
}

export interface DailyAdStats {
  date: string
  impressions: number
  clicks: number
  ctr: number
  estimated_revenue: number
  device_breakdown: Record<string, number>
  geo_breakdown: Record<string, number>
}

interface AdState {
  // Client-side tracking
  sessionId: string
  impressionsThisSession: number
  lastAdShownAt: number
  videosWatchedSinceAd: number
  // First-ever preroll in this browser uses videoAdWarmupCount as its
  // threshold instead of videoAdFrequency — see shouldShowVideoAd(). Once
  // true, every later preroll uses the regular cadence. Persisted (not
  // session-scoped) so the warm-up is a true "first time in this browser"
  // grace period, not something that resets every visit.
  hasShownFirstVideoAd: boolean

  // Settings (cached from server)
  videoAdFrequency: number // Show ad every N videos, AFTER the warm-up below
  // Videos watched before the very first preroll ever shows in this browser
  // — deliberately softer than the regular cadence ("don't let people feel
  // the ads on release," the reasoning that drove this whole warm-up
  // concept). Only applies once; every subsequent preroll uses
  // videoAdFrequency instead.
  videoAdWarmupCount: number
  videoAdCooldownMinutes: number // Min minutes between preroll ads (0 = no cooldown)
  unskippableDuration: number // Seconds
  isPremiumUser: boolean
  showPartnerFooter: boolean

  // Actions
  setSessionId: (id: string) => void
  incrementImpressions: () => void
  incrementVideosWatched: () => void
  resetVideosWatched: () => void
  markFirstVideoAdShown: () => void
  shouldShowVideoAd: () => boolean
  setIsPremiumUser: (isPremium: boolean) => void
  setVideoAdSettings: (frequency: number, duration: number, cooldownMinutes?: number, warmupCount?: number) => void
  setShowPartnerFooter: (show: boolean) => void
}

// Generate session ID
const generateSessionId = () => {
  return `sess_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`
}

export const useAdStore = create<AdState>()(
  persist(
    (set, get) => ({
      sessionId: generateSessionId(),
      impressionsThisSession: 0,
      lastAdShownAt: 0,
      videosWatchedSinceAd: 0,
      hasShownFirstVideoAd: false,
      videoAdFrequency: 8, // Default: show ad every 8 videos after the warm-up
      videoAdWarmupCount: 4, // Default: no ad until the 4th video, ever, in this browser
      videoAdCooldownMinutes: 0, // Default: no time cooldown, count alone decides
      unskippableDuration: 7, // Default: 7 seconds unskippable
      isPremiumUser: false,
      showPartnerFooter: true,

      setSessionId: (id) => set({ sessionId: id }),

      incrementImpressions: () =>
        set((state) => ({
          impressionsThisSession: state.impressionsThisSession + 1,
          lastAdShownAt: Date.now(),
        })),

      incrementVideosWatched: () =>
        set((state) => ({
          videosWatchedSinceAd: state.videosWatchedSinceAd + 1,
        })),

      resetVideosWatched: () => set({ videosWatchedSinceAd: 0 }),

      // Called only from the paths that actually rendered a preroll (not the
      // no-fill/network-error paths, which also reset the counter above but
      // never showed anything) — see the call sites in
      // booru-detail-modal.tsx. Marks the warm-up as spent so every later
      // preroll uses the regular cadence instead.
      markFirstVideoAdShown: () => set({ hasShownFirstVideoAd: true }),

      shouldShowVideoAd: () => {
        const state = get()
        // Force Ads test mode must win over the premium check, not lose to
        // it — this order was backwards (checked here first) and meant an
        // admin testing on their own premium account could never trigger a
        // video preroll no matter what, since the test-mode bypass one line
        // down was unreachable. Every other placement already lets test
        // mode override premium status; this was the one that didn't.
        if (isAdTestModeOn()) return true
        // Premium users don't see ads
        if (state.isPremiumUser) return false
        // The very first preroll ever shown in this browser uses the softer
        // warm-up threshold; every one after that uses the regular cadence.
        const threshold = state.hasShownFirstVideoAd ? state.videoAdFrequency : state.videoAdWarmupCount
        return (
          state.videosWatchedSinceAd >= threshold &&
          isAdCooldownExpired("video_preroll", state.videoAdCooldownMinutes)
        )
      },

      setIsPremiumUser: (isPremium) => set({ isPremiumUser: isPremium }),

      setVideoAdSettings: (frequency, duration, cooldownMinutes, warmupCount) =>
        set({
          videoAdFrequency: frequency,
          unskippableDuration: duration,
          ...(cooldownMinutes !== undefined ? { videoAdCooldownMinutes: cooldownMinutes } : {}),
          ...(warmupCount !== undefined ? { videoAdWarmupCount: warmupCount } : {}),
        }),

      setShowPartnerFooter: (show) => set({ showPartnerFooter: show }),
    }),
    {
      name: "goonru-ad-tracking",
      partialize: (state) => ({
        sessionId: state.sessionId,
        videosWatchedSinceAd: state.videosWatchedSinceAd,
        hasShownFirstVideoAd: state.hasShownFirstVideoAd,
        showPartnerFooter: state.showPartnerFooter,
      }),
    },
  ),
)

// API functions for ad tracking
export async function trackImpression(data: AdImpression): Promise<void> {
  try {
    await fetch("/api/ads/impression", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    })
  } catch (error) {
    console.error("Failed to track impression:", error)
  }
}

export async function trackClick(impressionId: string, campaignId: string, houseAdId?: string): Promise<void> {
  try {
    await fetch("/api/ads/click", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ impression_id: impressionId, campaign_id: campaignId, house_ad_id: houseAdId }),
    })
  } catch (error) {
    console.error("Failed to track click:", error)
  }
}

export async function fetchActiveAd(slotSlug: string): Promise<AdCampaign | null> {
  try {
    const response = await fetch(`/api/ads/serve?slot=${slotSlug}`)
    if (!response.ok) return null
    return response.json()
  } catch (error) {
    console.error("Failed to fetch ad:", error)
    return null
  }
}

export async function fetchInlineAds(count = 5): Promise<InlineAdCampaign[]> {
  try {
    const response = await fetch(`/api/ads/serve?slot=grid_inline&count=${count}`)
    if (!response.ok) return []
    return response.json()
  } catch (error) {
    console.error("Failed to fetch inline ads:", error)
    return []
  }
}

// House ads — self-promo cards that cycle in the grid.
// Images live in /public/ads/ad_house/ — every entry below used to point at
// a nonexistent /ads/glym-*.webp path (a naming mismatch with the actual
// files, which live under ad_house/ with different names entirely), so
// every house ad silently rendered as a bare gradient with no artwork —
// <Image>'s onError just hides the broken img, so nothing ever surfaced the
// failure. Fixed to point at the real files.
export const MOCK_INLINE_ADS = [
  {
    id: "house_membership",
    campaign_id: "camp_house_membership",
    type: "internal" as const,
    title: "Still gooning for free?",
    subtitle: "Glym is disappointed. Support the site and go ad-free from $5/mo.",
    image_url: "/ads/ad_house/glym_midbeat.webp",
    click_url: "/subscribe",
    cta_text: "Become a Member",
    background_gradient: "linear-gradient(160deg, #1a0a2e 0%, #0d0d1a 100%)",
    badge_text: "Upgrade",
  },
  {
    id: "house_discord",
    campaign_id: "camp_house_discord",
    type: "internal" as const,
    title: "10,000 goons can't be wrong.",
    subtitle: "Scripts, artists, requests, and Glym. Everything you need in one server.",
    image_url: "/ads/ad_house/discord_1.webp",
    click_url: DISCORD_INVITE_URL,
    cta_text: "Join the Discord",
    background_gradient: "linear-gradient(160deg, #23272a 0%, #5865F2 100%)",
    // "Upgrade" makes no sense on a free Discord invite — nothing here is
    // being upgraded. That badge was hardcoded per ad.type in
    // InlineGridAd rather than read from the ad itself, so every internal
    // house ad said "Upgrade" regardless of what it actually promoted.
    badge_text: "Community",
  },
  {
    id: "house_scripting",
    campaign_id: "camp_house_scripting",
    type: "internal" as const,
    title: "Your favorite video deserves a script.",
    subtitle: "Nobody made one. That's on you. The editor is free and takes 20 minutes.",
    image_url: "/ads/ad_house/scriptor_1.webp",
    click_url: "/editors",
    cta_text: "Open the Editor",
    background_gradient: "linear-gradient(160deg, #0a1628 0%, #1e3a5f 100%)",
    badge_text: "Free Tool",
  },
  {
    id: "house_bounties",
    campaign_id: "camp_house_bounties",
    type: "internal" as const,
    title: "Someone is paying for scripts right now.",
    subtitle: "Glym already got a payout. Open bounties are waiting.",
    image_url: "/ads/ad_house/bounties_!.webp",
    click_url: "/bounties",
    cta_text: "Browse Bounties",
    background_gradient: "linear-gradient(160deg, #1a1200 0%, #3d2800 100%)",
    badge_text: "Earn Money",
  },
  {
    id: "house_commissions",
    campaign_id: "camp_house_commissions",
    type: "internal" as const,
    title: "Creators eat here.",
    subtitle: "List your rates, set your slots, get paid. Free to join.",
    image_url: "/ads/ad_house/art_coms_1.webp",
    click_url: "/commissions",
    cta_text: "Open a Commission",
    background_gradient: "linear-gradient(160deg, #0a1f0a 0%, #1a3d1a 100%)",
    badge_text: "Creators",
  },
  {
    id: "house_goonpals",
    campaign_id: "camp_house_goonpals",
    type: "internal" as const,
    title: "Glym misses you. Genuinely.",
    subtitle: "Your AI companion has been waiting. They remember everything.",
    image_url: "/ads/ad_house/goonpal_!.webp",
    click_url: "/goonpals",
    cta_text: "Meet Your Goonpal",
    background_gradient: "linear-gradient(160deg, #1a0a1f 0%, #3d1a4a 100%)",
    badge_text: "AI Companion",
  },
]
